import React, { useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { message, Form, Modal as AntdModal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Modal } from 'ui';
import { Select, InputNumber, TextArea, DatePicker } from 'components/Form';
import apiClient from 'utils/apiClient';
import useHandleError from 'utils/useHandleError';
import moment from 'moment';
import { config } from 'config/config';

const formItemLayout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 14 },
};

interface EditGradeModalInterface {
    open: boolean;
    onCancel: any;
    attemptId: number;
    evaluationType?: string;
    initialGrade?: any;
    initialGradeDate?: string | null;
    initialGradeComments?: string | null;
    onSaved: Function;
}

/**
 * 24.3.9 - editing a completed grade (PowerAdmin / program Admin role).
 * Shared by all three grading types; posts to grading/edit-grade/{attemptId}.
 */
const EditGradeModal: React.FC<EditGradeModalInterface> = ({
    open,
    onCancel,
    attemptId,
    evaluationType,
    initialGrade,
    initialGradeDate,
    initialGradeComments,
    onSaved,
}) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const intl = useIntl();
    const [form] = Form.useForm();
    const [handleError] = useHandleError();

    const submitEdit = async (values: any) => {
        setIsLoading(true);
        try {
            await apiClient.request(`/api/v1/courses/grading/edit-grade/${attemptId}`, {
                grade: values.grade,
                grade_date: values.grade_date ? moment(values.grade_date).format(config.DBDateFormat) : null,
                grade_comments: values.grade_comments ?? null,
            }, 'POST');

            message.success(intl.formatMessage({ id: 'grading.grade_updated_success' }));
            onSaved();
        } catch (error) {
            handleError(error);
        } finally {
            setIsLoading(false);
        }
    };

    const confirmEdit = (values: any) => {
        AntdModal.confirm({
            title: intl.formatMessage({ id: 'grading.edit_grade' }),
            icon: <ExclamationCircleOutlined />,
            content: intl.formatMessage({ id: 'grading.edit_grade_confirm' }),
            okText: intl.formatMessage({ id: 'general.confirm' }),
            cancelText: intl.formatMessage({ id: 'general.cancel' }),
            onOk: () => submitEdit(values),
        });
    };

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            title={intl.formatMessage({ id: 'grading.edit_grade' })}
            okText={intl.formatMessage({ id: 'general.save' })}
            cancelText={intl.formatMessage({ id: 'general.cancel' })}
            okButtonProps={{ loading: isLoading }}
            onOk={() => form.submit()}
            destroyOnClose
        >
            <Form
                form={form}
                onFinish={confirmEdit}
                preserve={false}
                initialValues={{
                    grade: initialGrade,
                    grade_date: initialGradeDate ? moment(initialGradeDate) : null,
                    grade_comments: initialGradeComments ?? '',
                }}
            >
                {evaluationType === 'RANGE' ? (
                    <InputNumber
                        name="grade"
                        label={intl.formatMessage({ id: 'grading.grade' })}
                        customRules={[
                            {
                                required: true,
                                message: intl.formatMessage({ id: 'validation.field_required' }),
                            },
                        ]}
                        customLayout={formItemLayout}
                        min={0}
                        max={100}
                        formatter={(value: number) => `${value}%`}
                        parser={(value: string) => value!.replace('%', '')}
                    />
                ) : (
                    <Select
                        name="grade"
                        label={intl.formatMessage({ id: 'grading.grade' })}
                        validation={{ required: true }}
                        customLayout={formItemLayout}
                        manualOptions={{
                            PASSED: intl.formatMessage({ id: 'general.passed' }),
                            FAILED: intl.formatMessage({ id: 'general.failed' }),
                        }}
                    />
                )}
                <DatePicker
                    name="grade_date"
                    label={intl.formatMessage({ id: 'grading.grade_date' })}
                    customLayout={formItemLayout}
                    format={config.defaultDateFormat}
                />
                <TextArea
                    name="grade_comments"
                    label={intl.formatMessage({ id: 'grading.grade_comment' })}
                    customLayout={formItemLayout}
                    autoSize={{ minRows: 3 }}
                    maxLength={1000}
                    showCount
                />
            </Form>
        </Modal>
    );
};

export default EditGradeModal;
